import { Fragment, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Sparkle, ArrowClockwise } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";

const fmtD = (s) => (s && /^\d{4}-\d{2}-\d{2}/.test(s) ? `${s.slice(8, 10)}.${s.slice(5, 7)}.${s.slice(0, 4)}` : s);
const eur = (v) => (v == null || v === "" ? null : Number(v).toLocaleString("de-DE", { style: "currency", currency: "EUR" }));

function Facts({ ai }) {
  const rows = [
    ["Absender", [ai.sender_company, ai.sender_name].filter(Boolean).join(" · ")],
    ["Personen", (ai.person_names || []).join(", ")],
    ["Objekt", (ai.property_addresses || []).join(" / ")],
    ["Lage", ai.unit_hint],
    ["Aktenzeichen", (ai.case_numbers || []).join(", ")],
    ["Vertragsnr.", (ai.contract_numbers || []).join(", ")],
    ["Datum", fmtD(ai.document_date)], ["Fällig", fmtD(ai.due_date)],
    ["Betrag", eur(ai.amount_eur)], ["Rechnungsnr.", ai.invoice_number], ["IBAN", ai.iban],
  ].filter(([, v]) => v);
  return (
    <dl className="grid grid-cols-[110px_1fr] gap-x-2 gap-y-0.5 text-[12px]">
      {rows.map(([k, v]) => <Fragment key={k}><dt className="text-slate-500">{k}</dt><dd className="text-slate-800 break-words">{v}</dd></Fragment>)}
    </dl>
  );
}

export function ScanAiBox({ m, onUpdated }) {
  const [busy, setBusy] = useState(false);
  const ai = m.ai || {};
  const rerun = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/mailbox/messages/${m.id}/analyze`); onUpdated?.(data); toast.success("Dokument neu analysiert"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Analyse fehlgeschlagen"); }
    finally { setBusy(false); }
  };
  return (
    <div className="rounded-md border border-indigo-200 bg-indigo-50/50 p-3 space-y-2" data-testid="scan-ai-box">
      <div className="flex items-center gap-2 text-xs font-medium text-indigo-900">
        <Sparkle size={14} /> KI-Texterkennung
        {ai.doc_type_label && <span className="rounded-full bg-white border border-indigo-200 px-2 py-0.5 text-[11px]" data-testid="scan-doc-type">{ai.doc_type_label}</span>}
        <Button size="sm" variant="ghost" className="ml-auto h-6 gap-1 text-[11px]" onClick={rerun} disabled={busy} data-testid="scan-reanalyze-btn">
          <ArrowClockwise size={12} className={busy ? "animate-spin" : ""} /> Neu analysieren
        </Button>
      </div>
      {(ai.status === "pending" || ai.status === "running") && <div className="text-xs text-slate-600" data-testid="scan-ai-pending">Dokument wird gelesen…</div>}
      {ai.status === "failed" && <div className="text-xs text-rose-700" data-testid="scan-ai-failed">Analyse fehlgeschlagen – bitte manuell zuordnen oder neu analysieren.</div>}
      {ai.status === "done" && (<>
        {ai.summary && <p className="text-[13px] text-slate-800" data-testid="scan-ai-summary">{ai.summary}</p>}
        <Facts ai={ai} />
      </>)}
    </div>
  );
}

export function ScanPreview({ m }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let u = null;
    api.get(`/mailbox/messages/${m.id}/file`, { responseType: "blob" }).then(({ data }) => { u = URL.createObjectURL(data); setUrl(u); }).catch(() => setUrl(false));
    return () => { if (u) URL.revokeObjectURL(u); };
  }, [m.id]);
  if (url === false) return <div className="text-xs text-rose-700">Datei nicht ladbar.</div>;
  if (!url) return <div className="text-xs text-slate-500">Lade Vorschau…</div>;
  return (m.file?.content_type || "").startsWith("image/")
    ? <img src={url} alt={m.file?.name} className="max-h-[520px] border border-slate-200 rounded-md" data-testid="scan-preview" />
    : <iframe title="scan" src={url} className="w-full h-[520px] border border-slate-200 rounded-md bg-white" data-testid="scan-preview" />;
}
